'use client';

import React, { useState } from 'react';

import Image from 'next/image';

import rightArrow from '@/assets/images/right-arrow-icon.svg';
import HtmlRenderer from '@/components/HtmlRender';
import WebaliasLink from '@/components/Link/WebaliasLink';
import Container from '@/components/V2/Container';
import DetailModal from '@/components/V2/ModalContent';
import {
  IbmPlexItalic,
  IbmPlexItalicBold,
  IbmPlexItalicMedium,
  IbmPlexItalicSemiBold,
  SweetSansPro,
  SweetSansProBold,
  SweetSansProExtraLight,
  SweetSansProLight,
  SweetSansProMedium,
  SweetSansProSemiBold,
} from '@/config/fonts';
import cn from '@/helpers/utils/classNames';
import { MeetTheTeamResponse, TeamMember, TeamMemberProperty } from '@/types/MeetTheTeam';

interface TeamMembersProps {
  data: MeetTheTeamResponse;
}

const TeamMembers: React.FC<TeamMembersProps> = ({ data }) => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={cn(
        SweetSansProExtraLight.variable,
        SweetSansProLight.variable,
        SweetSansPro.variable,
        SweetSansProMedium.variable,
        SweetSansProSemiBold.variable,
        SweetSansProBold.variable,
        IbmPlexItalic.variable,
        IbmPlexItalicBold.variable,
        IbmPlexItalicMedium.variable,
        IbmPlexItalicSemiBold.variable
      )}
    >
      <div className="v2-bg-primary v2-py-[20px]">
        <Container>
          <h1 className="v2-text-[40px] v2-leading-[60px] v2-text-white v2-font-light v2-font-sweet-sans-pro-light v2-text-center">
            {data.name}
          </h1>
        </Container>
      </div>
      <div className="v2-py-[80px]">
        <Container>
          <div className="v2-grid v2-gap-[64px] v2-text-center">
            <div className="v2-text-[#101828] v2-font-normal v2-text-lg v2-font-sweet-sans-pro">
              {/* <div className="content-page-strong" dangerouslySetInnerHTML={{ __html: data.description.value }} /> */}
              <HtmlRenderer className="content-page-strong" html={data.description.value} />
            </div>
            <hr className="v2-border-gray-200"></hr>
            <div className="v2-gap-[32px] v2-flex v2-flex-wrap v2-justify-center">
              {data.members.map((member: TeamMember) => (
                <div
                  key={member.id}
                  className="v2-w-[338px] v2-cursor-pointer"
                  onClick={() => {
                    setSelectedMember(member);
                    setIsOpen(true);
                  }}
                >
                  <div className="v2-mb-[32px] v2-border-gray-200 v2-rounded-[8px] v2-shadow-lg v2-border-[8px] v2-h-[338px] v2-overflow-hidden">
                    <Image
                      alt="Member Image"
                      src={member.teamMemberImage}
                      height={338}
                      width={338}
                      className="h-[338px] w-[338px]"
                    />
                  </div>
                  <div>
                    <h3 className="v2-text-[#101828] v2-font-normal v2-text-3xl v2-mb-[8px] v2-font-sweet-sans-pro">
                      {member.name}
                    </h3>
                    <p className="v2-text-[#101828] v2-font-normal v2-text-lg v2-font-sweet-sans-pro v2-min-h-[56px]">
                      {member.designation}
                    </p>
                  </div>
                </div>
              ))}
              {selectedMember && (
                <DetailModal
                  isOpen={isOpen}
                  onClose={() => setIsOpen(!isOpen)}
                  hideMainScroll
                  className="v2-w-[640px] md:!v2-w-[640px] !v2-rounded-xs"
                >
                  <div className="v2-absolute v2-w-max v2-mx-auto v2-left-0 v2-right-0 v2-flex v2-flex-col v2-top-[-75px]">
                    <div className="v2-mx-auto v2-mb-[32px] v2-rounded-[4px] v2-mb-[16px] v2-w-[195px] v2-h-[195px] v2-overflow-hidden">
                      <Image
                        alt="Member Image"
                        src={selectedMember.teamMemberImage}
                        height={338}
                        width={338}
                        className="h-[338px] w-[338px]"
                      />
                    </div>
                    <div>
                      <h3 className="v2-text-primary v2-font-normal v2-text-3xl v2-mb-[8px] v2-font-sweet-sans-pro">
                        {selectedMember.name}
                      </h3>
                      <p className="v2-text-[#101828] v2-font-light v2-text-lg v2-font-sweet-sans-pro-light">
                        {selectedMember.designation}
                      </p>
                    </div>
                  </div>
                  <div className="v2-grid v2-gap-[16px] v2-grid-cols-1 v2-text-left v2-mt-[236px] v2-max-h-[450px] md:v2-max-h-[450px] sm:v2-max-h-[260px] xs:v2-max-h-[260px] v2-overflow-y-scroll custom-scroll">
                    <div className="v2-text-[#101828] v2-text-base v2-font-normal v2-font-sweet-sans-pro v2-grid v2-gap-[16px] v2-grid v2-grid-cols-1">
                      {selectedMember.properties.map((property: TeamMemberProperty) => (
                        <div key={property.id}>
                          <h4 className="v2-text-[#101828] v2-font-normal v2-text-lg v2-font-sweet-sans-pro">
                            {property.title}
                          </h4>
                          {/* <div className="v2-grid" dangerouslySetInnerHTML={{ __html: property.description.value }} />
                           */}
                          <HtmlRenderer className="v2-grid" html={property.description.value} />
                        </div>
                      ))}
                    </div>
                  </div>
                </DetailModal>
              )}
            </div>
          </div>
        </Container>
      </div>
      <div className="v2-py-[60px] v2-text-[#101828] v2-text-center v2-bg-[#F2F4F7]">
        <Container className="v2-grid v2-gap-[24px]">
          <h2 className="v2-text-[32px] v2-leading-[40px] v2-font-light v2-font-sweet-sans-pro-light">
            {data.Footer.heading}
          </h2>
          <p className="v2-text-lg v2-leading-[24px] v2-font-normal v2-font-sweet-sans-pro v2-max-w-[546px] v2-mx-auto">
            {/* <div dangerouslySetInnerHTML={{ __html: data.Footer.description.value }} /> */}
            <HtmlRenderer html={data.Footer.description.value} />
          </p>
          <WebaliasLink
            className="v2-mx-auto v2-font-sweet-sans-pro-medium v2-font-medium !v2-py-[10px] !v2-px-[18px] v2-rounded-xs v2-text-base v2-text-center v2-max-w-max v2-flex v2-items-center v2-justify-between v2-bg-primary hover:v2-bg-primary-700 v2-text-white"
            target="_blank"
            href={data.Footer.buttonLink}
            as={'a'}
          >
            {data.Footer.buttonText}
            <Image alt="Right Icon" src={rightArrow} height={20} width={20} className="v2-ml-[6px]" />
          </WebaliasLink>
        </Container>
      </div>
    </div>
  );
};

export default TeamMembers;
